/**
 * Connector vocabularies — kind, dialect and status — in ONE console home.
 *
 * ADR-0161 §5.1.5 creates this module; ADR-0154 Phase C8 specified its shape
 * and left it unbuilt (C8's own P4.6 note: "eight vocabularies outstanding,
 * each roughly one `hubStringArray` assertion" — connector dialects among
 * them). Kind needed a console home anyway, so dialect gets to come along and
 * one of the eight closes.
 *
 * WHAT WAS HERE BEFORE. The dialect union was hand-typed in TWO route files —
 * `AdminTenantDetails.tsx` (with its own options array) and
 * `TenantAdminConnectors.tsx` (with its own label map). Both were correct.
 * Both were correct the way every copy in the ADR-0154 audit was correct right
 * up until it wasn't: judgment-maintained copies drift, and the only ones that
 * hadn't were the ones a mechanism was holding.
 *
 * THE MECHANISM HERE IS G9 (cross-repo parity): `hubVocabParity.test.ts` reads
 * the hub's own source and fails when either list drifts from its owner. The
 * console is a separate repo and shares no workspace package with the hub, so
 * parity-by-reading is the sanctioned shape for value sets too small to be
 * worth an HTTP round trip (served-over-HTTP, G10, is first choice and is what
 * the larger vocabularies use).
 *
 * Label maps are exhaustive `Record`s, so adding a kind or dialect to the hub
 * fails `tsc` here rather than rendering a raw enum value at a user.
 */

// ---------------------------------------------------------------------------
// Connector kind (ADR-0161) — which system is on the other end
// ---------------------------------------------------------------------------

/** Hub owner: `src/repos/connectorKinds.ts` (CONNECTOR_KINDS). */
export const CONNECTOR_KINDS = ["generic", "plex"] as const;

export type ConnectorKind = (typeof CONNECTOR_KINDS)[number];

/** Hub owner: `KIND_LABELS`. Exhaustive — a new kind fails the build here. */
export const KIND_LABELS: Record<ConnectorKind, string> = {
  generic: "Generic SQL",
  plex: "Plex ERP",
};

/**
 * Options for a kind `<select>`. Kind is TOTAL on the row — there is no
 * "— none —" choice, unlike dialect, because every connector has a kind.
 */
export const CONNECTOR_KIND_OPTIONS: ReadonlyArray<{ value: ConnectorKind; label: string }> =
  CONNECTOR_KINDS.map((value) => ({ value, label: KIND_LABELS[value] }));

// ---------------------------------------------------------------------------
// Connector dialect (ADR-0056) — which SQL it speaks
// ---------------------------------------------------------------------------

/** Hub owner: `src/repos/connectors.ts` (CONNECTOR_DIALECTS). */
export const CONNECTOR_DIALECTS = ["sqlserver", "oracle", "mysql", "postgres", "snowflake", "duckdb"] as const;

export type ConnectorDialect = (typeof CONNECTOR_DIALECTS)[number];

export const DIALECT_LABELS: Record<ConnectorDialect, string> = {
  sqlserver: "SQL Server",
  oracle: "Oracle",
  mysql: "MySQL",
  postgres: "PostgreSQL",
  snowflake: "Snowflake",
  duckdb: "DuckDB",
};

/**
 * Options for a dialect `<select>`. Dialect is NULLABLE on the row, so the
 * surface adds its own empty choice — that is presentation, not vocabulary,
 * and stays in the component.
 */
export const CONNECTOR_DIALECT_OPTIONS: ReadonlyArray<{ value: ConnectorDialect; label: string }> =
  CONNECTOR_DIALECTS.map((value) => ({ value, label: DIALECT_LABELS[value] }));

// ---------------------------------------------------------------------------
// Connector status (ADR-0164 §5.2) — what the hub derives about reachability
// ---------------------------------------------------------------------------

/**
 * Hub owner: `src/repos/connectors.ts` (`DerivedConnectorStatus`), computed by
 * `deriveStatus` from heartbeat and poll staleness. The console never derives
 * it — every surface renders what the hub sends.
 *
 * WHAT WAS HERE BEFORE, and why this section exists. ADR-0164 §5.2 renamed the
 * connector's `healthy` boolean to `agent_reachable` and gave every connector
 * route this three-state `status` instead. Three surfaces were migrated;
 * `AdminStatus.tsx` was missed and went on reading `c.healthy`, which the hub
 * no longer sends — so from 2026-08-11 the System health page rendered
 * `undefined ? … : "Offline"` and reported EVERY connector offline, beside a
 * heartbeat timestamp from seconds earlier. The label and pill-class maps had
 * meanwhile been hand-copied into `AdminTenantDetails.tsx` and
 * `TenantAdminConnectors.tsx`, and a third copy in `ConnectorEventLog.tsx`
 * recovered the class by string-matching the LABEL ("Online" → pillOk), so the
 * pill colour travelled through display copy. All four are now this one home.
 */

/** Hub owner: `repos/connectors.ts` (`DerivedConnectorStatus`). */
export const CONNECTOR_STATUSES = ["online", "degraded", "offline"] as const;

export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

/** Exhaustive — a new hub status fails the build here rather than rendering raw. */
export const STATUS_LABELS: Record<ConnectorStatus, string> = {
  online: "Online",
  degraded: "Degraded",
  offline: "Offline",
};

/**
 * Pill classes. Presentation rather than vocabulary, but it lives beside the
 * labels because the copy that string-matched the label to get back here is
 * exactly the defect this section closes.
 */
export const STATUS_PILL_CLASSES: Record<ConnectorStatus, string> = {
  online: "pill pillOk",
  degraded: "pill pillWarn",
  offline: "pill pillBad",
};
